// src/admin/pages/supplier/SupplierDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  Stack,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Divider,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { Supplier } from '../purchase-orders/PurchaseOrder';
import { useSupplierMutations } from '../../../hooks/useSuppliers';

interface SupplierDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  supplier?: Supplier | null;
  onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const SupplierDrawer: React.FC<SupplierDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  supplier,
  onShowToast,
}) => {
  const { create, update } = useSupplierMutations();

  // ── Derived loading flag from mutations ───────────────────────────────────
  const loading = create.isPending || update.isPending;

  // ── Full-screen saving overlay (mirrors SuppliersManagement delete overlay) ─
  const [savingOverlay, setSavingOverlay] = useState(false);

  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    contactPerson: '',
    taxId: '',
    paymentTerms: '',
    isActive: true,
  });

  // Populate form when editing an existing supplier
  useEffect(() => {
    if (supplier) {
      setFormData({
        name:          supplier.name          || '',
        email:         supplier.email         || '',
        phone:         supplier.phone         || '',
        address:       supplier.address       || '',
        contactPerson: supplier.contactPerson || '',
        taxId:         supplier.taxId         || '',
        paymentTerms:  supplier.paymentTerms  || '',
        isActive:      supplier.isActive      ?? true,
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        contactPerson: '',
        taxId: '',
        paymentTerms: '',
        isActive: true,
      });
    }
    setError('');
  }, [supplier, open]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // ── Validation ────────────────────────────────────────────────────────
    if (!formData.name.trim()) {
      setError('Supplier name is required');
      onShowToast('Supplier name is required', 'error');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      onShowToast('Email is required', 'error');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      onShowToast('Please enter a valid email address', 'error');
      return;
    }

    if (!formData.phone.trim()) {
      setError('Phone is required');
      onShowToast('Phone is required', 'error');
      return;
    }

    setError('');

    // Show the full-screen overlay immediately before the async work begins,
    // mirroring the pattern in SuppliersManagement.handleDeleteConfirm where
    // setDeletingOverlay(true) is called before remove.mutateAsync().
    setSavingOverlay(true);

    try {
      if (supplier?.id) {
        // ── Update ────────────────────────────────────────────────────────
        const result = await update.mutateAsync({
          supplierId: supplier.id,
          supplierData: formData,
        });

        if (result.success) {
          onShowToast('Supplier updated successfully', 'success');
          onSuccess();
          handleClose();
        } else {
          const msg = result.message || 'Failed to update supplier';
          setError(msg);
          onShowToast(msg, 'error');
        }
      } else {
        // ── Create ────────────────────────────────────────────────────────
        const result = await create.mutateAsync({
          supplierData: formData,
        });

        if (result.success) {
          onShowToast('Supplier created successfully', 'success');
          onSuccess();
          handleClose();
        } else {
          const msg = result.message || 'Failed to create supplier';
          setError(msg);
          onShowToast(msg, 'error');
        }
      }
    } catch (err: any) {
      console.error('Error saving supplier:', err);
      const errorMessage = err.message || 'Failed to save supplier';
      setError(errorMessage);
      onShowToast(errorMessage, 'error');
    } finally {
      // Always hide the overlay once the async work finishes, whether success
      // or failure — mirrors the finally block in handleDeleteConfirm.
      setSavingOverlay(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      contactPerson: '',
      taxId: '',
      paymentTerms: '',
      isActive: true,
    });
    setError('');
    onClose();
  };

  const isEditing = !!supplier;

  // Label shown inside the overlay card — mirrors "Deleting Supplier…" text
  const overlayLabel = isEditing ? 'Updating Supplier…' : 'Creating Supplier…';

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* ── Drawer Header ── */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 1.5,
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <BusinessIcon sx={{ color: 'white', fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                    {isEditing ? 'Edit Supplier' : 'Add New Supplier'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isEditing
                      ? 'Update the supplier information below'
                      : 'Fill in the details to create a new supplier'}
                  </Typography>
                </Box>
              </Stack>
              <IconButton onClick={handleClose} disabled={loading} edge="end">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* ── Scrollable body ── */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 3,
              py: 3,
              minHeight: 0,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(128,128,128,0.3)', borderRadius: '3px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(128,128,128,0.5)' },
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2.5}>

              {/* ── Section: Core Details ── */}
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}
                >
                  Core Details
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Supplier Name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  disabled={loading}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  disabled={loading}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  disabled={loading}
                  size="small"
                />
              </Grid>

              {/* ── Section: Contact & Billing ── */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 0.5 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}
                >
                  Contact &amp; Billing
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contactPerson}
                  onChange={(e) => handleChange('contactPerson', e.target.value)}
                  disabled={loading}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tax ID"
                  value={formData.taxId}
                  onChange={(e) => handleChange('taxId', e.target.value)}
                  disabled={loading}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Payment Terms"
                  placeholder="e.g., Net 30"
                  value={formData.paymentTerms}
                  onChange={(e) => handleChange('paymentTerms', e.target.value)}
                  disabled={loading}
                  size="small"
                />
              </Grid>

              {/* ── Section: Address ── */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 0.5 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}
                >
                  Address
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={3}
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  disabled={loading}
                  size="small"
                />
              </Grid>

              {/* ── Section: Status ── */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 0.5 }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600, display: 'block', mb: 1 }}
                >
                  Status
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => handleChange('isActive', e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Active Supplier
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formData.isActive
                          ? 'Supplier is active and available for purchase orders'
                          : 'Supplier is inactive and will be hidden from order forms'}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>

            </Grid>
          </Box>

          {/* ── Drawer Footer ── */}
          <Divider />
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              gap: 1.5,
              justifyContent: 'flex-end',
              flexShrink: 0,
            }}
          >
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} /> : null}
            >
              {loading
                ? 'Saving...'
                : isEditing
                  ? 'Update Supplier'
                  : 'Add Supplier'}
            </Button>
          </Box>

        </Box>
      </Drawer>

      {/* ── Full-screen saving overlay ────────────────────────────────────────
          Rendered outside the Drawer so it sits above it in the stacking
          context — same pattern as the delete overlay in SuppliersManagement.
          zIndex is set above modal (drawer) level so it covers the drawer
          itself while the async operation is in flight.
      ─────────────────────────────────────────────────────────────────────── */}
      <Backdrop
        open={savingOverlay}
        sx={{
          zIndex: (t) => t.zIndex.modal + 100,
          flexDirection: 'column',
          gap: 3,
          bgcolor: 'rgba(0,0,0,0.6)',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}
            >
              {overlayLabel}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
            >
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
    </>
  );
};

export default SupplierDrawer;